import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col, Select, Button, Spin, message } from "antd";
import { errHandler } from "../../helper/helper";
import { getWasamaCustomerLoanDetails } from "./LoanService";
import { getAllWasmaByBranchIds } from "../General/GeneralService";
import WasamaCustomerLoanDetailsExcel from "../../Utils/Loan/WasamaCustomerLoanDetailsExcel";

const { Option } = Select;

const WasamaCustomerLoanDetails = ({
    loginPlaceId,
    districtData,
    districtName,
    divisionalAreaId,
    branchId,
    loanProducts,
}) => {
    const [loading, setLoading] = useState(false);
    const [availableDivisionals, setAvailableDivisionals] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableWasamas, setAvailableWasamas] = useState([]);
    const [isWasamaLoading, setIsWasamaLoading] = useState(false);

    const [selections, setSelections] = useState({
        districts: [],
        divisionals: [],
        branches: [],
        wasamas: [],
        products: [],
    });

    const isDistrictDisabled = loginPlaceId === "4" || loginPlaceId === "2" || loginPlaceId === "1";
    const isDivisionalDisabled = loginPlaceId === "2" || loginPlaceId === "1";
    const isBranchDisabled = loginPlaceId === "1";

    const showDistrict   = loginPlaceId === "3";
    const showDivisional = loginPlaceId === "3" || loginPlaceId === "4";
    const showBranch     = loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2";

    useEffect(() => {
        if (!districtData || districtData.length === 0) return;

        let initialDistricts = [];
        let initialDivisionals = [];
        let initialBranches = [];

        if (loginPlaceId === "4") {
            // District user – lock to their district
            const district = districtData.find(d => d.districtName === districtName);
            if (district) initialDistricts = [district.districtId];

        } else if (loginPlaceId === "2") {
            // Divisional user – lock to their district + divisional
            const district = districtData.find(d =>
                d.divisionalAreaDtoList?.some(da => da.divAId === parseInt(divisionalAreaId))
            );
            if (district) {
                initialDistricts  = [district.districtId];
                initialDivisionals = [parseInt(divisionalAreaId)];
            }

        } else if (loginPlaceId === "1") {
            // Branch user – lock to their district + divisional + branch
            const district = districtData.find(d =>
                d.divisionalAreaDtoList?.some(da =>
                    da.branchDtoList?.some(b => b.id === parseInt(branchId))
                )
            );
            if (district) {
                initialDistricts = [district.districtId];
                const da = district.divisionalAreaDtoList.find(da =>
                    da.branchDtoList?.some(b => b.id === parseInt(branchId))
                );
                if (da) {
                    initialDivisionals = [da.divAId];
                    initialBranches    = [parseInt(branchId)];
                }
            }
        }

        setSelections(prev => ({
            ...prev,
            districts:  initialDistricts,
            divisionals: initialDivisionals,
            branches:   initialBranches,
        }));
    }, [loginPlaceId, districtData, districtName, divisionalAreaId, branchId]);

    useEffect(() => {
        if (selections.districts.length > 0) {
            const nested = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);
            setAvailableDivisionals(nested);

            if (loginPlaceId !== "2" && loginPlaceId !== "1") {
                setSelections(prev => ({
                    ...prev,
                    divisionals: prev.divisionals.filter(id => nested.some(da => da.divAId === id)),
                }));
            }
        } else {
            setAvailableDivisionals([]);
            if (loginPlaceId === "3") {
                setSelections(prev => ({ ...prev, divisionals: [], branches: [], wasamas: [] }));
            }
        }
    }, [selections.districts, districtData, loginPlaceId]);

    useEffect(() => {
        if (selections.divisionals.length > 0) {
            const nested = availableDivisionals
                .filter(da => selections.divisionals.includes(da.divAId))
                .flatMap(da => da.branchDtoList || []);
            setAvailableBranches(nested);

            if (loginPlaceId !== "1") {
                setSelections(prev => ({
                    ...prev,
                    branches: prev.branches.filter(id => nested.some(b => b.id === id)),
                }));
            }
        } else {
            setAvailableBranches([]);
            if (loginPlaceId !== "1") {
                setSelections(prev => ({ ...prev, branches: [], wasamas: [] }));
            }
        }
    }, [selections.divisionals, availableDivisionals, loginPlaceId]);

    useEffect(() => {
        const fetchWasamas = async () => {
            if (selections.branches.length === 0) {
                setAvailableWasamas([]);
                setSelections(prev => ({ ...prev, wasamas: [] }));
                return;
            }
            setIsWasamaLoading(true);
            try {
                const res  = await getAllWasmaByBranchIds(selections.branches);
                const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                setAvailableWasamas(data);
                setSelections(prev => ({
                    ...prev,
                    wasamas: prev.wasamas.filter(id => data.some(w => w.wastId === id)),
                }));
            } catch (err) {
                console.error("Wasama fetch failed", err);
                message.error("Failed to fetch Wasamas");
            } finally {
                setIsWasamaLoading(false);
            }
        };
        fetchWasamas();
    }, [selections.branches]);

    const generateReport = async () => {
        if (selections.branches.length === 0 && selections.wasamas.length === 0) {
            message.error("Please select at least a Branch or Wasama");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                branchIdList: selections.branches.map(id => parseInt(id)),
                wasamIdList:  selections.wasamas.length  > 0 ? selections.wasamas.map(id  => parseInt(id)) : null,
                productIdList: selections.products.length > 0 ? selections.products.map(id => parseInt(id)) : null,
                includeAll: true,
            };

            const response = await getWasamaCustomerLoanDetails(payload);

            if (response && (response.status === 500 || response.status === 400)) {
                message.error(response.message || "Backend Error: " + response.status);
                return;
            }

            const reportData = response?.data || (Array.isArray(response) ? response : null);

            if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
                message.warning("No data found for the selected parameters");
                return;
            }

            const branchNames = availableBranches
                .filter(b => selections.branches.includes(b.id))
                .map(b => b.branchName);

            const selectedWasamaNames = selections.wasamas.length > 0
                ? availableWasamas.filter(w => selections.wasamas.includes(w.wastId)).map(w => w.wasName)
                : ["All Wasamas"];

            const selectedProductNames = selections.products.length > 0
                ? loanProducts.filter(p => selections.products.includes(p.productId)).map(p => p.productName)
                : ["All Products"];

            await WasamaCustomerLoanDetailsExcel(
                reportData,
                branchNames,
                selectedWasamaNames,
                selectedProductNames,
                new Date()
            );

            message.success("Report generated successfully");
        } catch (err) {
            errHandler(err);
        } finally {
            setLoading(false);
        }
    };

    const isGenerateDisabled = loading || (selections.branches.length === 0 && selections.wasamas.length === 0);

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>

                        {/* District Selection — Island (3) only */}
                        {showDistrict && (
                            <Col span={24}>
                                <Form.Item label="Select District(s)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select District(s)"
                                        style={{ width: "100%" }}
                                        value={selections.districts}
                                        onChange={(values) => {
                                            const allIds = districtData.map(d => d.districtId);
                                            if (values.includes("ALL")) {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    districts:  prev.districts.length === allIds.length ? [] : allIds,
                                                    divisionals: [],
                                                    branches:   [],
                                                    wasamas:    [],
                                                }));
                                            } else {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    districts:  values,
                                                    divisionals: [],
                                                    branches:   [],
                                                    wasamas:    [],
                                                }));
                                            }
                                        }}
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        maxTagCount="responsive"
                                    >
                                        <Option value="ALL">
                                            {selections.districts.length === districtData.length ? "Deselect All" : "Select All Districts"}
                                        </Option>
                                        {districtData.map(d => (
                                            <Option key={d.districtId} value={d.districtId}>{d.districtName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Divisional Selection — Island (3) + District (4) */}
                        {showDivisional && (
                            <Col span={24}>
                                <Form.Item label="Select Divisional Area(s)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select Divisional Area(s)"
                                        style={{ width: "100%" }}
                                        value={selections.divisionals}
                                        onChange={(values) => {
                                            const allIds = availableDivisionals.map(da => da.divAId);
                                            if (values.includes("ALL")) {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    divisionals: prev.divisionals.length === allIds.length ? [] : allIds,
                                                    branches:    [],
                                                    wasamas:     [],
                                                }));
                                            } else {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    divisionals: values,
                                                    branches:    [],
                                                    wasamas:     [],
                                                }));
                                            }
                                        }}
                                        disabled={loginPlaceId === "4" && selections.districts.length === 0}
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        maxTagCount="responsive"
                                    >
                                        <Option value="ALL">
                                            {selections.divisionals.length === availableDivisionals.length ? "Deselect All" : "Select All Divisionals"}
                                        </Option>
                                        {availableDivisionals.map(da => (
                                            <Option key={da.divAId} value={da.divAId}>{da.divAIdName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Branch Selection — Island (3), District (4), Divisional (2) */}
                        {showBranch && (
                            <Col span={24}>
                                <Form.Item label="Select Branch(es)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select Branch(es)"
                                        style={{ width: "100%" }}
                                        value={selections.branches}
                                        onChange={(values) => {
                                            const allIds = availableBranches.map(b => b.id);
                                            if (values.includes("ALL")) {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    branches: prev.branches.length === allIds.length ? [] : allIds,
                                                    wasamas:  [],
                                                }));
                                            } else {
                                                setSelections(prev => ({
                                                    ...prev,
                                                    branches: values,
                                                    wasamas:  [],
                                                }));
                                            }
                                        }}
                                        disabled={loginPlaceId === "2" && selections.divisionals.length === 0}
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        maxTagCount="responsive"
                                    >
                                        <Option value="ALL">
                                            {selections.branches.length === availableBranches.length ? "Deselect All" : "Select All Branches"}
                                        </Option>
                                        {availableBranches.map(b => (
                                            <Option key={b.id} value={b.id}>{b.branchName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Wasama Selection — All roles */}
                        <Col span={24}>
                            <Form.Item label="Select Wasama(s)">
                                <Select
                                    mode="multiple"
                                    placeholder={isWasamaLoading ? "Loading Wasamas..." : "Select Wasama(s) (optional)"}
                                    style={{ width: "100%" }}
                                    value={selections.wasamas}
                                    onChange={(values) => {
                                        const allIds = availableWasamas.map(w => w.wastId);
                                        if (values.includes("ALL")) {
                                            setSelections(prev => ({
                                                ...prev,
                                                wasamas: prev.wasamas.length === allIds.length ? [] : allIds,
                                            }));
                                        } else {
                                            setSelections(prev => ({ ...prev, wasamas: values }));
                                        }
                                    }}
                                    maxTagCount="responsive"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    loading={isWasamaLoading}
                                    disabled={selections.branches.length === 0}
                                >
                                    {availableWasamas.length > 0 && (
                                        <Option value="ALL">
                                            {selections.wasamas.length === availableWasamas.length ? "Deselect All" : "Select All Wasamas"}
                                        </Option>
                                    )}
                                    {availableWasamas.map(w => (
                                        <Option key={w.wastId} value={w.wastId}>{w.wasName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Products Selection — All roles */}
                        <Col span={24}>
                            <Form.Item label="Select Product(s)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select products (optional)"
                                    style={{ width: "100%" }}
                                    value={selections.products}
                                    onChange={(values) => {
                                        const allIds = loanProducts.map(p => p.productId);
                                        if (values.includes("ALL")) {
                                            setSelections(prev => ({
                                                ...prev,
                                                products: prev.products.length === allIds.length ? [] : allIds,
                                            }));
                                        } else {
                                            setSelections(prev => ({ ...prev, products: values }));
                                        }
                                    }}
                                    maxTagCount="responsive"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    <Option value="ALL">
                                        {selections.products.length === loanProducts.length ? "Deselect All" : "Select All Products"}
                                    </Option>
                                    {loanProducts.map(p => (
                                        <Option key={p.productId} value={p.productId}>
                                            {p.productCode} - {p.productName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Generate Button */}
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                    size="large"
                                    block
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default WasamaCustomerLoanDetails;